import React, { useEffect, useState } from "react";
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRoles,
  fetchPermissions,
  fetchRoleById,
  updateRole,
} from "../../features/admin/RoleSlice";
import { toast } from "react-toastify";

// Các quyền cũ, không hiển thị
const deprecatedPermissions = ["CHANGE_FEE_USER"];

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

/** ====== TUỲ BIẾN: Thứ tự ưu tiên theo subtext (slug) ======
 * Role nào khớp subtext ở vị trí trước sẽ đứng trước.
 * Ưu tiên: exact → startsWith → includes
 */
const PRIORITY_SUBTEXTS = [
  "chu-tich",
  "pho-chu-tich-thuong-truc",
  "pho-chu-tich",
  "truong-ban",
  "uy-vien",
  "van-phong",
  "ke-toan",
  "hoi-vien",
];
const toSlug = (str) => {
  if (!str) return "";
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};
const getPriorityIndex = (roleName) => {
  const slug = toSlug(roleName || "");
  let idx = PRIORITY_SUBTEXTS.findIndex((sub) => slug === sub);
  if (idx !== -1) return idx;
  idx = PRIORITY_SUBTEXTS.findIndex((sub) => slug.startsWith(sub));
  if (idx !== -1) return idx;
  idx = PRIORITY_SUBTEXTS.findIndex((sub) => slug.includes(sub));
  return idx === -1 ? Number.POSITIVE_INFINITY : idx;
};

const RolePermissionCard = () => {
  const dispatch = useDispatch();
  const { roles, permissions } = useSelector((state) => state.roles);

  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  // Dạng { [roleId]: { ...roleDetail } }
  const [roleDetailsList, setRoleDetailsList] = useState({});
  // Thông tin role đang xem/sửa chi tiết
  const [selectedRoleDetail, setSelectedRoleDetail] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  // === Sort roles theo ưu tiên subtext rồi theo tên (dùng cho Tabs) ===
  const sortedRoles = (Array.isArray(roles) ? [...roles] : []).sort((a, b) => {
    const pa = getPriorityIndex(a?.roleName);
    const pb = getPriorityIndex(b?.roleName);
    if (pa !== pb) return pa - pb;
    return (a?.roleName || "").localeCompare(b?.roleName || "", "vi", {
      sensitivity: "base",
    });
  });

  // Group permission SYSTEM
  const groupedPermissionsTypeSystem =
    permissions
      ?.filter((perm) => perm.type === "System")
      ?.reduce((acc, perm) => {
        if (perm.parentId === null) {
          acc[perm.id] = { ...perm, children: [] };
        } else {
          acc[perm.parentId]?.children?.push(perm);
        }
        return acc;
      }, {}) || {};

  // Group permission USER
  const groupedPermissionsUser =
    permissions
      ?.filter((perm) => perm.type === "User")
      ?.reduce((acc, perm) => {
        if (perm.parentId === null) {
          acc[perm.id] = { ...perm, children: [] };
        } else {
          acc[perm.parentId]?.children?.push(perm);
        }
        return acc;
      }, {}) || {};

  // Gọi API roles và permissions khi mount
  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchPermissions());
  }, [dispatch]);

  // Khi mở modal: fetch quyền từng role (preload hết 1 lần)
  useEffect(() => {
    if (open && sortedRoles?.length > 0) {
      const fetchAllRoleDetails = async () => {
        setLoading(true);
        const details = {};
        for (const role of sortedRoles) {
          try {
            const res = await dispatch(fetchRoleById(role.id)).unwrap();
            details[role.id] = res;
          } catch {
            details[role.id] = null;
          }
        }
        setRoleDetailsList(details);
        // Tab đầu tiên theo thứ tự đã sort
        if (sortedRoles[0]) {
          const first = details[sortedRoles[0].id];
          setSelectedRoleDetail(first || null);
          setSelectedPermissions(
            first?.permissions?.map((p) =>
              typeof p === "object" ? p.id : p,
            ) || [],
          );
          setSelectedTab(0);
        }
        setLoading(false);
      };
      fetchAllRoleDetails();
    }
    // eslint-disable-next-line
  }, [open, roles]); // roles đổi → sortedRoles sẽ đổi theo

  // Đổi tab role, lấy quyền từ local state
  const handleSelectRoleTab = (idx) => {
    setSelectedTab(idx);
    const role = sortedRoles[idx];
    if (role && roleDetailsList[role.id]) {
      setSelectedRoleDetail(roleDetailsList[role.id]);
      setSelectedPermissions(
        roleDetailsList[role.id]?.permissions?.map((p) =>
          typeof p === "object" ? p.id : p,
        ) || [],
      );
    }
  };

  // Toggle quyền
  const togglePermission = (id, children = []) => {
    setSelectedPermissions((prev) =>
      prev.includes(id)
        ? prev.filter((perm) => perm !== id && !children?.includes(perm))
        : [...prev, id, ...children.filter((child) => !prev.includes(child))],
    );
  };

  // Lưu phân quyền (updateRole), update lại local state cho role đó
  const handleSave = async () => {
    if (!selectedRoleDetail) return;
    setLoading(true);
    try {
      const dataUpdate = {
        id: selectedRoleDetail.id,
        roleName: selectedRoleDetail.roleName,
        roleSlug: selectedRoleDetail.roleSlug,
        permissionIds: selectedPermissions,
      };
      await dispatch(updateRole(dataUpdate)).unwrap();
      toast.success("Cập nhật phân quyền thành công!");
      // Update lại local state
      setRoleDetailsList((prev) => ({
        ...prev,
        [selectedRoleDetail.id]: {
          ...selectedRoleDetail,
          permissions: permissions.filter((p) =>
            selectedPermissions.includes(p.id),
          ),
        },
      }));
    } catch (err) {
      toast.error("Lỗi khi lưu phân quyền!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Card */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full h-full sm:w-auto shadow-md flex flex-col items-center text-center p-5 rounded-2xl border border-emerald-100  transition-all cursor-pointer"
        onClick={() => setOpen(true)}
      >
        <div className="p-3 bg-emerald-50 rounded-full mb-3 mt-4">
          <AdjustmentsHorizontalIcon className="h-6 w-6 text-emerald-600" />
        </div>
        <p className="text-lg text-black mb-1">Phân quyền chức năng</p>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-[1580px] mx-2 rounded-2xl p-10 border border-emerald-100"
              style={{ maxHeight: "90vh", overflow: "auto" }}
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Quyền chức năng theo vai trò
              </h2>

              {/* Tabs (đã sắp xếp) */}
              <div className="flex gap-1 flex-wrap border-b border-emerald-100 mb-6">
                {sortedRoles?.map((role, idx) => (
                  <button
                    key={role.id}
                    onClick={() => handleSelectRoleTab(idx)}
                    className={`px-6 py-2 text-base font-semibold rounded-t-md
                      ${
                        selectedTab === idx
                          ? "bg-emerald-50 text-emerald-700 border-b-2 border-emerald-400"
                          : "bg-white text-gray-500 hover:bg-emerald-50"
                      }
                    `}
                  >
                    {role.roleName}
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="text-center text-lg text-gray-500 py-10">
                  Đang tải dữ liệu...
                </div>
              ) : selectedRoleDetail ? (
                <>
                  {/* ================= BẢNG PHÂN QUYỀN ================= */}
                  <div className="mb-4 px-0">
                    <h3 className="text-lg font-medium mb-4">
                      Phân quyền hệ thống
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
                      {Object.values(groupedPermissionsTypeSystem).map(
                        (group) => (
                          <div key={group.id} className="pb-4 mb-4 rounded-lg">
                            <label className="flex items-center gap-2 font-semibold">
                              <input
                                type="checkbox"
                                className="checkbox checkbox-info"
                                checked={
                                  group.children?.length > 0
                                    ? group.children.every((perm) =>
                                        selectedPermissions.includes(perm.id),
                                      )
                                    : selectedPermissions.includes(group.id)
                                }
                                onChange={() =>
                                  group.children?.length > 0
                                    ? togglePermission(
                                        group.id,
                                        group.children
                                          .sort((a, b) =>
                                            a.permissionName.localeCompare(
                                              b.permissionName,
                                            ),
                                          )
                                          .map((perm) => perm.id),
                                      )
                                    : togglePermission(group.id)
                                }
                              />
                              {group.permissionName}
                            </label>
                            <div className="grid grid-cols-1 gap-2 mt-2 ml-6">
                              {group.children
                                .sort((a, b) =>
                                  a.permissionName.localeCompare(
                                    b.permissionName,
                                  ),
                                )
                                .map((perm) =>
                                  !deprecatedPermissions.includes(
                                    perm.permissionSlug,
                                  ) ? (
                                    <label
                                      key={perm.id}
                                      className="flex items-center gap-2"
                                    >
                                      <input
                                        type="checkbox"
                                        className="checkbox checkbox-info"
                                        checked={selectedPermissions.includes(
                                          perm.id,
                                        )}
                                        onChange={() =>
                                          togglePermission(perm.id)
                                        }
                                      />
                                      {perm.permissionName}
                                    </label>
                                  ) : null,
                                )}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                    <hr />
                    <h3 className="text-lg font-medium mb-4 pt-2">
                      Phân quyền bài viết
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.values(groupedPermissionsUser).map((group) => (
                        <div key={group.id} className="pb-2 rounded-lg">
                          <label className="flex items-center gap-2 font-semibold">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-info"
                              checked={
                                group.children?.length > 0
                                  ? group.children.every((perm) =>
                                      selectedPermissions.includes(perm.id),
                                    )
                                  : selectedPermissions.includes(group.id)
                              }
                              onChange={() =>
                                group.children?.length > 0
                                  ? togglePermission(
                                      group.id,
                                      group.children.map((perm) => perm.id),
                                    )
                                  : togglePermission(group.id)
                              }
                            />
                            {group.permissionName}
                          </label>
                          <div className="grid grid-cols-1 gap-2 mt-2 ml-4">
                            {group.children.map((perm) => (
                              <label
                                key={perm.id}
                                className="flex items-center gap-2"
                              >
                                <input
                                  type="checkbox"
                                  className="checkbox checkbox-info"
                                  checked={selectedPermissions.includes(
                                    perm.id,
                                  )}
                                  onChange={() => togglePermission(perm.id)}
                                />
                                {perm.permissionName}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* ============ HẾT BẢNG PHÂN QUYỀN =============== */}
                  <div className="flex justify-end mt-6">
                    <button
                      type="button"
                      className="btn btn-primary px-6 py-2"
                      disabled={loading}
                      onClick={handleSave}
                    >
                      {loading ? "Đang lưu..." : "Lưu"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center text-lg text-gray-500 py-10">
                  Không có dữ liệu chức vụ
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RolePermissionCard;
